/**
 * URL-scheme allowlist for values rendered into anchor `href` attributes.
 * FHIR `ContactPoint.value` and `Attachment.url` are free-text fields that can
 * carry attacker-controlled `javascript:`/`data:` schemes, which execute on
 * click. Pass user/FHIR-sourced URLs through `getSafeHref` before binding them
 * to an `href`.
 *
 * @agentic @benchmark/ui
 */

/** Schemes permitted in rendered anchor `href` attributes. @agentic @benchmark/ui */
const ALLOWED_SCHEMES = ["http", "https", "mailto", "tel"];

/**
 * Returns the URL when it is safe to use as an anchor `href`, otherwise
 * `undefined`. Strips characters browsers ignore inside schemes (control
 * chars), rejects protocol-relative URLs, and allows only an explicit
 * http/https/mailto/tel scheme (schemeless relative paths are passed through).
 * @agentic @benchmark/ui
 */
export const getSafeHref = (raw?: string | null): string | undefined => {
  if (!raw) return undefined;

  // Remove control characters that browsers ignore inside a scheme
  // (e.g. "java\tscript:" / "java\nscript:") so they cannot smuggle a
  // disallowed scheme past the check below.
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\u0000-\u001f\u007f-\u009f]/g, "").trim();
  if (!cleaned) return undefined;

  // Reject protocol-relative URLs (`//evil.com`); they navigate off-origin.
  if (cleaned.startsWith("//")) return undefined;

  const schemeMatch = /^([a-z][a-z0-9+.-]*):/i.exec(cleaned);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (!ALLOWED_SCHEMES.includes(scheme)) return undefined;
  }

  return cleaned;
};
